import React, { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import AuthImg from "@/components/AuthImg";
import { compressImage } from "@/lib/imageCompression";
import {
  User as UserIcon, Camera, Loader2, Save, Trash2, MapPin, Phone, Mail,
  Banknote, ShieldCheck, Bell, Lock, KeyRound, Upload, Eye, EyeOff,
  CheckCircle2, XCircle, Plug, Building2, Sparkles, CreditCard, RotateCcw, PenLine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import SubscriptionCancellationCard from "@/components/SubscriptionCancellationCard";
import SignaturePad from "react-signature-canvas";

const Section = ({ icon: Icon, title, description, children, footer }) => (
  <section className="rounded-2xl border border-zinc-200 bg-white" data-testid={`settings-section-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <header className="px-6 pt-5 pb-4 border-b border-zinc-100 flex items-start gap-3">
      {Icon && (
        <div className="h-9 w-9 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-white" strokeWidth={1.8} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
        {description && <p className="text-sm text-zinc-500 mt-0.5">{description}</p>}
      </div>
    </header>
    <div className="p-6">{children}</div>
    {footer && <footer className="px-6 py-4 border-t border-zinc-100 bg-zinc-50/40 rounded-b-2xl">{footer}</footer>}
  </section>
);

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11px] text-zinc-400 mt-1">{hint}</p>}
  </div>
);

export default function Settings() {
  const { user, refreshMe, accessToken, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [enhanceState, setEnhanceState] = useState(null); // null | {status:"working"|"ready", original, enhanced}
  const [dragActive, setDragActive] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);
  const fileInputRef = useRef(null);
  const letterheadInputRef = useRef(null);
  const [letterheadUploading, setLetterheadUploading] = useState(false);

  // ===== System API-Keys (admin-only DAT) + per-user Versicherungszugang =====
  const isAdmin = user?.role === "admin";
  const [apiSettings, setApiSettings] = useState({});       // DAT (admin)
  const [apiAvailable, setApiAvailable] = useState({});
  const [apiDraft, setApiDraft] = useState({});
  const [apiReveal, setApiReveal] = useState({});
  const [apiSaving, setApiSaving] = useState(false);

  // GDV per-user
  const [gdvStatus, setGdvStatus] = useState({ has_gdv: false, gdv_requestor: "" });
  const [gdvDraft, setGdvDraft] = useState({ gdv_requestor: "", gdv_password: "" });
  const [gdvRequest, setGdvRequest] = useState(null);   // user's own application status
  const [gdvRequestBusy, setGdvRequestBusy] = useState(false);
  const [gdvSign, setGdvSign] = useState(null);          // {signed_at, signer_name, sent_at, sent_to}
  const [gdvSignBusy, setGdvSignBusy] = useState(false);
  const [gdvSaving, setGdvSaving] = useState(false);
  const [gdvRevealPwd, setGdvRevealPwd] = useState(false);

  // Admin-only: GDV-Status aller Nutzer
  const [adminGdvUsers, setAdminGdvUsers] = useState([]);

  useEffect(() => {
    let abort = false;
    api.get("/users/me/profile")
      .then((r) => { if (!abort) setProfile(r.data.user); })
      .catch((e) => toast.error(apiError(e)));
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/users/me/gdv-credentials").then(({ data }) => {
      setGdvStatus(data);
      setGdvDraft({ gdv_requestor: data.gdv_requestor || "", gdv_password: "" });
    }).catch(() => {});
    api.get("/gdv-requests/me").then(({ data }) => {
      setGdvRequest(data.request);
      setGdvSign({
        signed_at: data.signature?.signed_at || null,
        signer_name: data.signature?.signer_name || null,
        sent_at: data.antrag_sent_at || null,
        sent_to: data.antrag_sent_to || null,
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestGdv = async () => {
    setGdvRequestBusy(true);
    try {
      const { data } = await api.post("/gdv-requests/me");
      setGdvRequest(data.request);
      toast.success("Antrag gestellt — der Admin bearbeitet ihn jetzt");
    } catch (e) {
      toast.error(apiError(e));
    } finally { setGdvRequestBusy(false); }
  };

  const signGdv = async ({ dataUrl, signer }) => {
    setGdvSignBusy(true);
    try {
      const { data } = await api.post("/gdv-requests/me/sign", {
        signature_data_url: dataUrl,
        signer_name: signer,
      });
      if (data.sent) {
        toast.success(`Antrag unterschrieben und an ${data.sent_to || "die GDV-Stelle"} gesendet.`);
      } else {
        toast.success("Unterschrift gespeichert — der Antrag wird automatisch versendet, sobald Ihr Profil (Adresse) vollständig ist.");
      }
      const { data: d2 } = await api.get("/gdv-requests/me");
      setGdvRequest(d2.request);
      setGdvSign({
        signed_at: d2.signature?.signed_at || null,
        signer_name: d2.signature?.signer_name || null,
        sent_at: d2.antrag_sent_at || null,
        sent_to: d2.antrag_sent_to || null,
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally { setGdvSignBusy(false); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/settings").then(({ data }) => {
      setApiAvailable(data.available_keys || {});
      setApiSettings(data.settings || {});
    }).catch(() => { /* settings endpoint requires admin; ignore */ });
    api.get("/users/admin/gdv-status").then(({ data }) => {
      setAdminGdvUsers(data.users || []);
    }).catch(() => {});
  }, [isAdmin]);

  if (!profile) {
    return (
      <Layout title="Einstellungen">
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
      </Layout>
    );
  }

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = (({ name, company, phone, mobile, address_line, postal_code, city, country,
        iban, bic, bank_name, vat_id, tax_number,
        notify_email, notify_case_updates, notify_marketing }) => ({
        name, company, phone, mobile, address_line, postal_code, city, country,
        iban, bic, bank_name, vat_id, tax_number,
        notify_email, notify_case_updates, notify_marketing,
      }))(profile);
      const { data } = await api.patch("/users/me/profile", payload);
      setProfile(data.user);
      await refreshMe?.();
      toast.success("Profil gespeichert");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const compressed = await compressImage(file, { maxDimension: 512, quality: 0.86 });
      const fd = new FormData();
      fd.append("file", compressed, compressed.name || file.name);
      const { data } = await api.post("/users/me/avatar", fd, {
      });
      setProfile((p) => ({ ...p, avatar_path: data.avatar_path }));
      await refreshMe?.();
      toast.success("Profilbild aktualisiert");
      // Automatisch KI-Verbesserung anstoßen
      enhanceAvatar(data.avatar_path);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const enhanceAvatar = async (originalPath) => {
    setEnhanceState({ status: "working", original: originalPath || profile?.avatar_path, enhanced: null });
    try {
      const { data } = await api.post("/users/me/avatar/enhance", {}, { timeout: 120000 });
      setEnhanceState({ status: "ready", original: data.original_path, enhanced: data.enhanced_path });
    } catch (e) {
      setEnhanceState(null);
      toast.error(apiError(e, "KI-Verbesserung fehlgeschlagen"));
    }
  };

  const applyEnhanced = async () => {
    try {
      const { data } = await api.post("/users/me/avatar/enhance/apply");
      setProfile((p) => ({ ...p, avatar_path: data.avatar_path }));
      await refreshMe?.();
      toast.success("KI-Version übernommen ✨");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEnhanceState(null);
    }
  };

  const removeAvatar = async () => {
    if (!profile.avatar_path) return;
    if (!window.confirm("Profilbild entfernen?")) return;
    try {
      await api.delete("/users/me/avatar");
      setProfile((p) => ({ ...p, avatar_path: null }));
      await refreshMe?.();
      toast.success("Profilbild entfernt");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const uploadLetterhead = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen (PNG/JPG/WebP)");
      return;
    }
    setLetterheadUploading(true);
    try {
      const compressed = await compressImage(file, { maxDimension: 1200, quality: 0.9 });
      const fd = new FormData();
      fd.append("file", compressed, compressed.name || file.name);
      const { data } = await api.post("/users/me/letterhead-logo", fd);
      setProfile((p) => ({ ...p, letterhead_logo_path: data.letterhead_logo_path }));
      await refreshMe?.();
      toast.success("Briefkopf-Logo aktualisiert");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLetterheadUploading(false);
      if (letterheadInputRef.current) letterheadInputRef.current.value = "";
    }
  };
  const removeLetterhead = async () => {
    if (!profile.letterhead_logo_path) return;
    if (!window.confirm("Briefkopf-Logo entfernen? Im Gutachten erscheint dann wieder das Easy-Gutachter-Logo.")) return;
    try {
      await api.delete("/users/me/letterhead-logo");
      setProfile((p) => ({ ...p, letterhead_logo_path: null }));
      await refreshMe?.();
      toast.success("Briefkopf-Logo entfernt");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const changePassword = async () => {
    if (!pwd.current || !pwd.next) return toast.error("Bitte alle Passwortfelder ausfüllen");
    if (pwd.next !== pwd.confirm) return toast.error("Neue Passwörter stimmen nicht überein");
    if (pwd.next.length < 8) return toast.error("Mindestens 8 Zeichen");
    setPwdSaving(true);
    try {
      await api.post("/users/me/password", { current_password: pwd.current, new_password: pwd.next });
      setPwd({ current: "", next: "", confirm: "" });
      toast.success("Passwort geändert");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPwdSaving(false);
    }
  };

  const initials = (profile.name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  const saveApiKeys = async () => {
    if (Object.keys(apiDraft).length === 0) return toast.info("Keine Änderungen");
    setApiSaving(true);
    try {
      await api.patch("/settings", apiDraft);
      const { data } = await api.get("/settings");
      setApiSettings(data.settings || {});
      setApiDraft({});
      toast.success("API-Keys gespeichert");
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setApiSaving(false);
    }
  };

  const saveGdv = async () => {
    if (!gdvDraft.gdv_requestor && !gdvDraft.gdv_password) {
      return toast.info("Bitte Versicherungszugang und Passwort ausfüllen");
    }
    setGdvSaving(true);
    try {
      const payload = {};
      if (gdvDraft.gdv_requestor !== undefined) payload.gdv_requestor = gdvDraft.gdv_requestor;
      if (gdvDraft.gdv_password) payload.gdv_password = gdvDraft.gdv_password;
      await api.patch("/users/me/gdv-credentials", payload);
      const { data } = await api.get("/users/me/gdv-credentials");
      setGdvStatus(data);
      setGdvDraft({ gdv_requestor: data.gdv_requestor || "", gdv_password: "" });
      if (isAdmin) {
        api.get("/users/admin/gdv-status").then(({ data: d2 }) => {
          setAdminGdvUsers(d2.users || []);
        }).catch(() => {});
      }
      toast.success("Versicherungszugang gespeichert");
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setGdvSaving(false);
    }
  };

  const clearGdv = async () => {
    if (!window.confirm("Versicherungszugangsdaten wirklich entfernen?")) return;
    setGdvSaving(true);
    try {
      await api.patch("/users/me/gdv-credentials", { gdv_requestor: "", gdv_password: "" });
      setGdvStatus({ has_gdv: false, gdv_requestor: "" });
      setGdvDraft({ gdv_requestor: "", gdv_password: "" });
      if (isAdmin) {
        api.get("/users/admin/gdv-status").then(({ data: d2 }) => {
          setAdminGdvUsers(d2.users || []);
        }).catch(() => {});
      }
      toast.success("Versicherungszugang entfernt");
    } catch (e) {
      toast.error(apiError(e, "Entfernen fehlgeschlagen"));
    } finally {
      setGdvSaving(false);
    }
  };

  return (
    <Layout title="Einstellungen" subtitle="Profil, Kontaktdaten, Bankverbindung, Sicherheit">
      <div className="max-w-4xl">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-6 flex flex-wrap h-auto bg-zinc-100 p-1 rounded-xl">
            <TabsTrigger value="profile" data-testid="tab-profile" className="flex items-center gap-1.5"><UserIcon className="h-3.5 w-3.5" /> Profil</TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact" className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Adresse & Kontakt</TabsTrigger>
            <TabsTrigger value="bank" data-testid="tab-bank" className="flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5" /> Bankdaten</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Sicherheit</TabsTrigger>
            <TabsTrigger value="subscription" data-testid="tab-subscription" className="flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Abonnement</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications" className="flex items-center gap-1.5"><Bell className="h-3.5 w-3.5" /> Benachrichtigungen</TabsTrigger>
            <TabsTrigger value="apikeys" data-testid="tab-apikeys" className="flex items-center gap-1.5"><Plug className="h-3.5 w-3.5" /> API-Zugänge</TabsTrigger>
          </TabsList>

          {/* ============ PROFILE ============ */}
          <TabsContent value="profile" className="space-y-6">
            <Section
              icon={UserIcon}
              title="Profilbild"
              description="Wird in der Kopfzeile, im Gutachten-PDF und in E-Mails verwendet."
            >
              <div className="flex items-start gap-5 flex-wrap">
                <label
                  htmlFor="avatar-file-input"
                  className={cn(
                    "group relative h-32 w-32 rounded-full overflow-hidden border-2 border-dashed flex items-center justify-center shrink-0 cursor-pointer transition",
                    dragActive
                      ? "border-emerald-500 bg-emerald-50/60 ring-4 ring-emerald-100"
                      : "border-zinc-300 bg-zinc-50 hover:border-emerald-400 hover:bg-emerald-50/30"
                  )}
                  data-testid="avatar-dropzone"
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                    const file = e.dataTransfer?.files?.[0];
                    if (!file) return;
                    if (!file.type.startsWith("image/")) {
                      toast.error("Bitte eine Bilddatei wählen (JPG/PNG/WebP)");
                      return;
                    }
                    uploadAvatar(file);
                  }}
                >
                  {profile.avatar_path ? (
                    <AuthImg path={profile.avatar_path} token={accessToken} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center px-2 pointer-events-none">
                      <Upload className={cn(
                        "h-7 w-7 mx-auto transition-colors",
                        dragActive ? "text-emerald-600" : "text-zinc-400 group-hover:text-emerald-500"
                      )} strokeWidth={1.6} />
                      <div className="font-display font-bold text-xl text-zinc-500 mt-1">{initials}</div>
                    </div>
                  )}
                  {/* Hover overlay (when an image is set) */}
                  {profile.avatar_path && !avatarUploading && (
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <Camera className="h-7 w-7 text-white" strokeWidth={1.8} />
                    </div>
                  )}
                  {/* Drag-active overlay */}
                  {dragActive && (
                    <div className="absolute inset-0 bg-emerald-500/30 backdrop-blur-[2px] flex items-center justify-center pointer-events-none">
                      <div className="text-center">
                        <Upload className="h-8 w-8 text-emerald-700 mx-auto" strokeWidth={1.8} />
                        <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider mt-1">Loslassen</div>
                      </div>
                    </div>
                  )}
                  {avatarUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="h-7 w-7 text-white animate-spin" />
                    </div>
                  )}
                </label>

                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-semibold text-zinc-900 text-base">Profilbild hochladen</div>
                  <p className="text-sm text-zinc-500 mt-0.5">
                    Foto auf das Avatar-Feld <strong>ziehen & ablegen</strong> oder Button anklicken.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button onClick={() => fileInputRef.current?.click()} disabled={avatarUploading} data-testid="upload-avatar-btn">
                      <Camera className="h-4 w-4 mr-2" /> Foto auswählen
                    </Button>
                    {profile.avatar_path && (
                      <Button
                        variant="outline"
                        className="text-violet-700 border-violet-200 hover:bg-violet-50"
                        onClick={() => enhanceAvatar()}
                        disabled={avatarUploading || enhanceState?.status === "working"}
                        data-testid="enhance-avatar-btn"
                      >
                        <Sparkles className="h-4 w-4 mr-2" /> Mit KI verbessern
                      </Button>
                    )}
                    {profile.avatar_path && (
                      <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={removeAvatar} data-testid="remove-avatar-btn">
                        <Trash2 className="h-4 w-4 mr-2" /> Entfernen
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-2">
                    JPG / PNG / WebP · max. 10 MB · wird auf 512 px komprimiert ·
                    nach dem Upload optimiert die <strong>KI</strong> dein Foto automatisch ✨
                  </p>
                  <input
                    id="avatar-file-input"
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadAvatar(e.target.files?.[0])}
                    data-testid="avatar-file-input"
                  />
                </div>
              </div>
            </Section>

            {/* KI-Verbesserung: Vorher/Nachher-Dialog */}
            <Dialog open={!!enhanceState} onOpenChange={(o) => { if (!o && enhanceState?.status !== "working") setEnhanceState(null); }}>
              <DialogContent className="sm:max-w-lg" data-testid="enhance-avatar-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-violet-600" /> KI-Foto-Verbesserung
                  </DialogTitle>
                  <DialogDescription>
                    {enhanceState?.status === "working"
                      ? "Die KI optimiert dein Foto — Beleuchtung, Schärfe und Hintergrund. Das dauert ca. 10–20 Sekunden…"
                      : "Vergleiche Original und KI-Version — du entscheidest, welche verwendet wird."}
                  </DialogDescription>
                </DialogHeader>
                {enhanceState?.status === "working" ? (
                  <div className="py-10 text-center" data-testid="enhance-working">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-violet-600" />
                    <div className="text-sm text-zinc-500 mt-3">Foto wird verbessert…</div>
                  </div>
                ) : enhanceState?.status === "ready" ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-400 mb-1.5 text-center">Original</div>
                        <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-50">
                          <AuthImg path={enhanceState.original} token={accessToken} alt="Original" className="w-full h-full object-cover" />
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-violet-600 mb-1.5 text-center">✨ KI-Version</div>
                        <div className="aspect-square rounded-2xl overflow-hidden border-2 border-violet-300 bg-zinc-50">
                          <AuthImg path={enhanceState.enhanced} token={accessToken} alt="KI-Version" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end mt-2">
                      <Button variant="outline" onClick={() => setEnhanceState(null)} data-testid="enhance-keep-original-btn">
                        Original behalten
                      </Button>
                      <Button onClick={applyEnhanced} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="enhance-apply-btn">
                        <Sparkles className="h-4 w-4 mr-2" /> KI-Version verwenden
                      </Button>
                    </div>
                  </>
                ) : null}
              </DialogContent>
            </Dialog>

            <Section
              icon={Camera}
              title="Briefkopf-Logo (Gutachten)"
              description="Dieses Logo erscheint oben links auf jeder Seite deiner Gutachten-Vorschau und PDFs. Wenn keins hochgeladen ist, wird das Easy-Gutachter-Logo verwendet."
            >
              <div className="flex items-center gap-5">
                <label
                  htmlFor="letterhead-file-input"
                  className="group relative w-40 h-20 rounded-xl bg-white border-2 border-dashed border-zinc-200 hover:border-cyan-400 cursor-pointer flex items-center justify-center overflow-hidden transition-colors"
                  data-testid="letterhead-dropzone"
                >
                  {profile.letterhead_logo_path ? (
                    <AuthImg path={profile.letterhead_logo_path} token={accessToken} alt="Briefkopf" className="w-full h-full object-contain p-2" />
                  ) : (
                    <div className="text-center px-2 pointer-events-none">
                      <Upload className="h-6 w-6 mx-auto text-zinc-400 group-hover:text-cyan-600 transition-colors" strokeWidth={1.6} />
                      <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-wider">Logo auswählen</div>
                    </div>
                  )}
                  {letterheadUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    </div>
                  )}
                </label>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-semibold text-zinc-900 text-base">Eigenes Briefkopf-Logo</div>
                  <p className="text-sm text-zinc-500 mt-0.5">PNG / JPG mit transparentem Hintergrund — wirkt am besten im Querformat.</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button onClick={() => letterheadInputRef.current?.click()} disabled={letterheadUploading} data-testid="upload-letterhead-btn">
                      <Camera className="h-4 w-4 mr-2" /> Logo auswählen
                    </Button>
                    {profile.letterhead_logo_path && (
                      <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={removeLetterhead} data-testid="remove-letterhead-btn">
                        <Trash2 className="h-4 w-4 mr-2" /> Entfernen
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-2">max. 10 MB · wird auf 1200 px komprimiert</p>
                  <input
                    ref={letterheadInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadLetterhead(e.target.files?.[0])}
                    data-testid="letterhead-file-input"
                  />
                </div>
              </div>
            </Section>

            <Section
              icon={UserIcon}
              title="Persönliche Daten"
              description="Diese Angaben erscheinen in Gutachten und Rechnungen."
              footer={
                <div className="flex justify-end">
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              }
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name"><Input data-testid="field-name" value={profile.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
                <Field label="Büro / Firma"><Input data-testid="field-company" value={profile.company || ""} onChange={(e) => set("company", e.target.value)} /></Field>
                <Field label="E-Mail" hint="Anmelde-Adresse · kann nicht direkt geändert werden">
                  <Input data-testid="field-email" value={profile.email || ""} readOnly className="bg-zinc-50" />
                </Field>
                <Field label="Rolle">
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 capitalize" data-testid="field-role">
                    <ShieldCheck className="h-3 w-3 mr-1" /> {profile.role}
                  </Badge>
                </Field>
                <Field label="USt-IdNr."><Input data-testid="field-vat-id" placeholder="DE123456789" value={profile.vat_id || ""} onChange={(e) => set("vat_id", e.target.value.toUpperCase())} /></Field>
                <Field label="Steuernummer"><Input data-testid="field-tax-number" placeholder="33/123/45678" value={profile.tax_number || ""} onChange={(e) => set("tax_number", e.target.value)} /></Field>
              </div>
            </Section>

            <SignatureProfileSection profile={profile} onSaved={(u) => setProfile(u)} />
          </TabsContent>

          {/* ============ CONTACT / ADDRESS ============ */}
          <TabsContent value="contact" className="space-y-6">
            <Section
              icon={Phone}
              title="Telefon & Mobil"
              description="Diese Nummern werden in Gutachten und im E-Mail-Footer angezeigt."
              footer={
                <div className="flex justify-end">
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-contact-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              }
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Telefon (Festnetz)"><Input data-testid="field-phone" placeholder="+49 30 1234567" value={profile.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
                <Field label="Mobil"><Input data-testid="field-mobile" placeholder="+49 170 1234567" value={profile.mobile || ""} onChange={(e) => set("mobile", e.target.value)} /></Field>
              </div>
            </Section>

            <Section
              icon={MapPin}
              title="Geschäftsadresse"
              description="Wird auf Rechnungen und PDF-Gutachten verwendet."
              footer={
                <div className="flex justify-end">
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-address-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              }
            >
              <div className="grid sm:grid-cols-3 gap-5">
                <div className="sm:col-span-3">
                  <Field label="Straße & Hausnummer"><Input data-testid="field-address-line" value={profile.address_line || ""} onChange={(e) => set("address_line", e.target.value)} /></Field>
                </div>
                <Field label="PLZ"><Input data-testid="field-postal-code" maxLength={10} value={profile.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} /></Field>
                <div className="sm:col-span-2">
                  <Field label="Stadt"><Input data-testid="field-city" value={profile.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
                </div>
                <div className="sm:col-span-3">
                  <Field label="Land"><Input data-testid="field-country" value={profile.country || ""} onChange={(e) => set("country", e.target.value)} /></Field>
                </div>
              </div>
            </Section>
          </TabsContent>

          {/* ============ BANK ============ */}
          <TabsContent value="bank" className="space-y-6">
            <Section
              icon={Banknote}
              title="Bankverbindung"
              description="IBAN/BIC werden auf Rechnungen automatisch als Zahlungsadresse eingedruckt."
              footer={
                <div className="flex justify-end">
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-bank-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              }
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Field label="Kontoinhaber / Bankname">
                    <Input data-testid="field-bank-name" placeholder="z. B. Sparkasse Berlin" value={profile.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="IBAN" hint="Wird verschlüsselt gespeichert.">
                    <Input data-testid="field-iban" placeholder="DE12 3456 7890 1234 5678 90" value={profile.iban || ""} onChange={(e) => set("iban", e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, ""))} className="font-mono tracking-wider" />
                  </Field>
                </div>
                <Field label="BIC"><Input data-testid="field-bic" placeholder="ABCDEDEXXX" value={profile.bic || ""} onChange={(e) => set("bic", e.target.value.toUpperCase())} className="font-mono tracking-wider" /></Field>
              </div>
            </Section>
          </TabsContent>

          {/* ============ SECURITY ============ */}
          <TabsContent value="security" className="space-y-6">
            <Section
              icon={KeyRound}
              title="Passwort ändern"
              description="Mindestens 8 Zeichen. Empfohlen: Mix aus Buchstaben, Zahlen und Sonderzeichen."
              footer={
                <div className="flex justify-end">
                  <Button onClick={changePassword} disabled={pwdSaving} data-testid="save-password-btn">
                    {pwdSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                    Passwort ändern
                  </Button>
                </div>
              }
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Field label="Aktuelles Passwort">
                    <Input type="password" data-testid="field-pwd-current" autoComplete="current-password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
                  </Field>
                </div>
                <Field label="Neues Passwort">
                  <Input type="password" data-testid="field-pwd-new" autoComplete="new-password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} />
                </Field>
                <Field label="Neues Passwort bestätigen">
                  <Input type="password" data-testid="field-pwd-confirm" autoComplete="new-password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} />
                </Field>
              </div>
            </Section>

            <Section
              icon={ShieldCheck}
              title="Datenschutz"
              description="Auf Anfrage löschen oder exportieren wir alle deine Daten DSGVO-konform."
            >
              <p className="text-sm text-zinc-600">
                Schreibe an <a className="text-emerald-700 hover:underline" href="mailto:datenschutz@easy-gutachter.de">datenschutz@easy-gutachter.de</a>, um eine Datenkopie oder die Löschung anzufordern.
              </p>
            </Section>

            <DeleteAccountCard onLogout={logout} />
          </TabsContent>

          {/* ============ SUBSCRIPTION ============ */}
          <TabsContent value="subscription" className="space-y-6">
            <SubscriptionCancellationCard />
          </TabsContent>

          {/* ============ NOTIFICATIONS ============ */}
          <TabsContent value="notifications" className="space-y-6">
            <Section
              icon={Bell}
              title="Benachrichtigungen"
              description="Bestimme, wann wir dich per E-Mail kontaktieren."
              footer={
                <div className="flex justify-end">
                  <Button onClick={saveProfile} disabled={saving} data-testid="save-notifications-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              }
            >
              <div className="space-y-4 max-w-lg">
                <NotifyRow
                  icon={Mail}
                  title="System-E-Mails"
                  description="Wichtige Meldungen, Login-Sicherheit, Stornos."
                  checked={profile.notify_email !== false}
                  onChange={(v) => set("notify_email", v)}
                  testid="notify-email"
                />
                <NotifyRow
                  icon={Bell}
                  title="Akten-Updates"
                  description="Status-Änderungen, Rückfragen vom Backoffice."
                  checked={profile.notify_case_updates !== false}
                  onChange={(v) => set("notify_case_updates", v)}
                  testid="notify-case-updates"
                />
                <NotifyRow
                  icon={Mail}
                  title="Marketing & Tipps"
                  description="Produkt-News, Schulungs-Termine, Sonderangebote."
                  checked={!!profile.notify_marketing}
                  onChange={(v) => set("notify_marketing", v)}
                  testid="notify-marketing"
                />
              </div>
            </Section>
          </TabsContent>

          {/* ============ API-ZUGÄNGE ============ */}
          <TabsContent value="apikeys" className="space-y-6">

            {/* Rechtssicherer GDV-Antrag: erst zweckgebunden unterschreiben,
                dann automatischer Versand an kundenbetreuung@gdv-dl.de. */}
            {!gdvStatus.has_gdv && !gdvSign?.signed_at ? (
              <GdvSignCard defaultName={profile?.name} busy={gdvSignBusy} onSubmit={signGdv} />
            ) : (
              <>
                <GdvRequestCard
                  request={gdvRequest}
                  hasCreds={gdvStatus.has_gdv}
                  onCreate={requestGdv}
                  busy={gdvRequestBusy}
                />
                {gdvSign?.signed_at && !gdvStatus.has_gdv && (
                  <div
                    className="rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-xs text-emerald-900 flex items-center gap-2"
                    data-testid="gdv-sign-status"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>
                      Antrag elektronisch unterschrieben am {new Date(gdvSign.signed_at).toLocaleDateString("de-DE")}
                      {gdvSign.sent_at
                        ? ` — versendet an ${gdvSign.sent_to || "die GDV-Stelle"} am ${new Date(gdvSign.sent_at).toLocaleDateString("de-DE")}.`
                        : " — wird automatisch versendet, sobald Ihr Profil (Adresse) vollständig ist."}
                    </span>
                  </div>
                )}
              </>
            )}

            {/* Versicherungszugang — per User (alle Rollen, also auch Admin) */}
            <Section
              icon={Plug}
              title="Versicherungszugang beantragen"
              description="Persönliche Zugangsdaten für die Versicherungsabfrage. Bei Bedarf testet der Admin mit eigenen Credentials, falls deine Abfrage einmal fehlschlägt."
              footer={
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-xs text-zinc-500">
                    {gdvStatus.has_gdv ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" /> Hinterlegt — bereit für Abfragen
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-zinc-500">
                        <XCircle className="h-4 w-4" /> Noch keine Zugangsdaten gespeichert
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {gdvStatus.has_gdv && (
                      <Button
                        variant="outline"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={clearGdv}
                        disabled={gdvSaving}
                        data-testid="gdv-clear-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Entfernen
                      </Button>
                    )}
                    <Button onClick={saveGdv} disabled={gdvSaving} data-testid="gdv-save-btn">
                      {gdvSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Versicherungszugang speichern
                    </Button>
                  </div>
                </div>
              }
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Versicherungszugang" hint="Versicherungszugang eintragen">
                  <Input
                    data-testid="gdv-requestor-input"
                    placeholder="z. B. mustermann_kfz"
                    value={gdvDraft.gdv_requestor}
                    onChange={(e) => setGdvDraft((d) => ({ ...d, gdv_requestor: e.target.value }))}
                  />
                </Field>
                <Field
                  label={gdvStatus.has_gdv ? "Passwort (neu setzen)" : "Passwort"}
                  hint={gdvStatus.has_gdv ? "Leer lassen, wenn unverändert." : "Wird verschlüsselt gespeichert."}
                >
                  <div className="flex gap-2">
                    <Input
                      type={gdvRevealPwd ? "text" : "password"}
                      autoComplete="new-password"
                      data-testid="gdv-password-input"
                      placeholder={gdvStatus.has_gdv ? "••••••••" : "Passwort"}
                      value={gdvDraft.gdv_password}
                      onChange={(e) => setGdvDraft((d) => ({ ...d, gdv_password: e.target.value }))}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setGdvRevealPwd((v) => !v)}
                      title={gdvRevealPwd ? "Verbergen" : "Anzeigen"}
                      data-testid="gdv-password-toggle"
                    >
                      {gdvRevealPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </Field>
              </div>
            </Section>

            {/* DAT — System-weit, nur Admin */}
            {isAdmin && (
              <Section
                icon={Building2}
                title="DAT-Zugang (system-weit)"
                description="Wird vom gesamten Team genutzt. Nur Admins können diese Zugangsdaten ändern."
                footer={
                  <div className="flex justify-end">
                    <Button onClick={saveApiKeys} disabled={apiSaving} className="brand-gradient-bg text-white" data-testid="apikeys-save-btn">
                      {apiSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      DAT-Zugang speichern
                    </Button>
                  </div>
                }
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  {Object.entries(apiAvailable).map(([key, meta]) => {
                    const isSecret = !!meta.secret;
                    const placeholder = apiSettings[key] || "Noch nicht hinterlegt";
                    const draftVal = apiDraft[key] ?? "";
                    return (
                      <div key={key} data-testid={`apikey-row-${key}`} className={isSecret ? "sm:col-span-2" : ""}>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{meta.label || key}</Label>
                        <div className="mt-1.5 flex gap-2">
                          <Input
                            type={isSecret && !apiReveal[key] ? "password" : "text"}
                            value={draftVal}
                            onChange={(e) => setApiDraft((d) => ({ ...d, [key]: e.target.value }))}
                            placeholder={placeholder}
                            data-testid={`apikey-input-${key}`}
                          />
                          {isSecret && (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => setApiReveal((r) => ({ ...r, [key]: !r[key] }))}
                              title={apiReveal[key] ? "Verbergen" : "Anzeigen"}
                              data-testid={`apikey-toggle-${key}`}
                            >
                              {apiReveal[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          )}
                        </div>
                        {meta.description && (
                          <div className="text-[11px] text-zinc-400 mt-1">{meta.description}</div>
                        )}
                        {apiSettings[key] && !draftVal && (
                          <div className="text-[11px] text-zinc-500 mt-1">
                            Aktueller Wert: <code className="font-mono">{apiSettings[key]}</code>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {Object.keys(apiAvailable).length === 0 && (
                    <div className="sm:col-span-2 text-sm text-zinc-500">Keine Schlüssel-Definitionen geladen.</div>
                  )}
                </div>
              </Section>
            )}

            {/* Versicherungszugang-Übersicht — nur Admin */}
            {isAdmin && (
              <Section
                icon={ShieldCheck}
                title="Versicherungszugang — Status der Nutzer"
                description="Zeigt, welche Mitglieder eigene Versicherungszugangsdaten hinterlegt haben."
              >
                {adminGdvUsers.length === 0 ? (
                  <div className="text-sm text-zinc-500">Noch keine Nutzer geladen.</div>
                ) : (
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm" data-testid="admin-gdv-table">
                      <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
                        <tr className="border-b border-zinc-100">
                          <th className="py-2 px-2 text-left font-semibold">Nutzer</th>
                          <th className="py-2 px-2 text-left font-semibold">Rolle</th>
                          <th className="py-2 px-2 text-left font-semibold">Zugangskennung</th>
                          <th className="py-2 px-2 text-left font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminGdvUsers.map((u) => (
                          <tr key={u.id} className="border-b border-zinc-50" data-testid={`admin-gdv-row-${u.id}`}>
                            <td className="py-2 px-2">
                              <div className="font-semibold text-zinc-900">{u.name || "—"}</div>
                              <div className="text-xs text-zinc-500">{u.email}</div>
                            </td>
                            <td className="py-2 px-2 capitalize text-zinc-600">{u.role}</td>
                            <td className="py-2 px-2 font-mono text-xs text-zinc-700">{u.gdv_requestor || "—"}</td>
                            <td className="py-2 px-2">
                              {u.has_gdv ? (
                                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Aktiv
                                </Badge>
                              ) : (
                                <Badge className="bg-zinc-100 text-zinc-500 border border-zinc-200">
                                  <XCircle className="h-3 w-3 mr-1" /> Fehlt
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

/**
 * Selbstlöschung des Kontos (Apple 5.1.1(v)-konform) mit optionaler Churn-Umfrage.
 * Flow: Card → 1) Grund wählen (überspringbar) → 2) „LÖSCHEN"-Text-Bestätigung
 */
function DeleteAccountCard({ onLogout }) {
  const REASONS = [
    { id: "too_expensive",    label: "Zu teuer" },
    { id: "not_enough_use",   label: "Nicht genug Aufträge / brauche es aktuell nicht" },
    { id: "missing_feature",  label: "Es fehlt eine Funktion, die ich brauche", needsComment: true },
    { id: "bug_or_problem",   label: "Ein Bug oder Problem hat mich gestört", needsComment: true },
    { id: "found_alternative",label: "Ich nutze jetzt eine andere Software" },
    { id: "just_testing",     label: "Habe mich nur zum Testen registriert" },
    { id: "other",            label: "Anderer Grund", needsComment: true },
  ];

  const [step, setStep] = React.useState(0); // 0=closed, 1=survey, 2=confirm
  const [reason, setReason] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [confirmText, setConfirmText] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const doDelete = async (skipFeedback = false) => {
    setBusy(true);
    try {
      const payload = skipFeedback ? {} : { reason: reason || undefined, comment: comment || undefined };
      const { data } = await api.delete("/users/me", { data: payload });
      toast.success("Konto und alle Daten gelöscht.", {
        description: data.cascaded_cases > 0 ? `${data.cascaded_cases} Akten wurden mitentfernt.` : "Danke für dein Feedback — bis bald!",
      });
      try { await onLogout(); } catch (_) { /* logout auf Server bereits durch Delete implizit */ }
      setTimeout(() => { window.location.href = "/login"; }, 800);
    } catch (e) {
      toast.error(apiError(e, "Konto konnte nicht gelöscht werden"));
      setBusy(false);
    }
  };

  const selectedReason = REASONS.find((r) => r.id === reason);
  const needsComment = !!selectedReason?.needsComment;

  return (
    <section
      className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-5 sm:p-6"
      data-testid="delete-account-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
          <Trash2 className="h-4 w-4 text-rose-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-zinc-900">Konto endgültig löschen</div>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
            Löscht dein Konto und alle personenbezogenen Daten (Akten, Fotos, Rechnungen,
            SEPA-Mandate, GDV-Antragsdaten, Sitzungen). Aktive Stripe-Abos werden sofort gekündigt.
            <strong className="text-rose-700"> Diese Aktion ist unwiderruflich.</strong>
          </p>

          {step === 0 && (
            <Button
              variant="outline"
              className="mt-4 border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800"
              onClick={() => setStep(1)}
              data-testid="delete-account-open-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Konto löschen …
            </Button>
          )}

          {step === 1 && (
            <div className="mt-4 space-y-3" data-testid="delete-account-survey">
              <div className="text-sm font-semibold text-zinc-800">Bevor du gehst — dürfen wir kurz fragen, warum?</div>
              <div className="text-xs text-zinc-500 -mt-1">
                Anonym, freiwillig, hilft uns Easy Gutachter zu verbessern.
              </div>
              <div className="space-y-1.5 pt-1">
                {REASONS.map((r) => (
                  <label
                    key={r.id}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition ${reason === r.id ? "border-rose-400 bg-white" : "border-zinc-200 bg-white/60 hover:border-zinc-300"}`}
                    data-testid={`delete-reason-${r.id}`}
                  >
                    <input
                      type="radio"
                      name="delete-reason"
                      value={r.id}
                      checked={reason === r.id}
                      onChange={(e) => setReason(e.target.value)}
                      className="h-4 w-4 text-rose-600 focus:ring-rose-500"
                    />
                    <span className="text-sm text-zinc-800">{r.label}</span>
                  </label>
                ))}
              </div>
              {needsComment && (
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Was genau? (optional)"
                  rows={3}
                  className="mt-2 w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-400"
                  maxLength={1000}
                  data-testid="delete-account-comment"
                />
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={() => setStep(2)}
                  disabled={busy}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="delete-account-survey-next-btn"
                >
                  Weiter zum Löschen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  disabled={busy}
                  data-testid="delete-account-survey-skip-btn"
                >
                  Ohne Angabe fortfahren
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setStep(0); setReason(""); setComment(""); }}
                  disabled={busy}
                  className="text-zinc-500"
                  data-testid="delete-account-survey-cancel-btn"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-4 space-y-3" data-testid="delete-account-confirm-step">
              {reason && (
                <div className="rounded-lg bg-white border border-zinc-200 px-3 py-2 text-xs text-zinc-500">
                  Grund: <span className="text-zinc-800 font-medium">{selectedReason?.label}</span>
                </div>
              )}
              <p className="text-sm text-zinc-700">
                Zur Sicherheit gib bitte <code className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-semibold">LÖSCHEN</code> ein:
              </p>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="LÖSCHEN"
                className="max-w-xs"
                data-testid="delete-account-confirm-input"
                autoFocus
              />
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  variant="destructive"
                  onClick={() => doDelete(false)}
                  disabled={busy || confirmText !== "LÖSCHEN"}
                  data-testid="delete-account-confirm-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Endgültig löschen
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setStep(0); setConfirmText(""); setReason(""); setComment(""); }}
                  disabled={busy}
                  data-testid="delete-account-cancel-btn"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}



function NotifyRow({ icon: Icon, title, description, checked, onChange, testid }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-zinc-100 p-4">
      <div className="h-9 w-9 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-zinc-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-sm">{title}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}


/**
 * Status-Card für den Antrag auf GDV-Zugangsdaten.
 *  - Wenn User bereits Credentials hinterlegt: Erfolgs-Card.
 *  - Wenn Antrag existiert: zeigt Pipeline + nächsten Schritt an.
 *  - Sonst: CTA "Antrag erstellen".
 */
/* Rechtssicherer GDV-Antrag: zweckgebundene elektronische Unterschrift
   mit Audit-Trail (Zeitstempel + IP) — erst danach wird der Antrag
   automatisch an kundenbetreuung@gdv-dl.de versendet. */
function GdvSignCard({ defaultName, busy, onSubmit }) {
  const sigRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [signer, setSigner] = useState(defaultName || "");

  useEffect(() => {
    if (defaultName && !signer) setSigner(defaultName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultName]);

  const clear = () => { sigRef.current?.clear(); setSigEmpty(true); };

  const submit = () => {
    if (sigEmpty || !sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Bitte zuerst im Feld unterschreiben.");
      return;
    }
    if (!signer.trim()) {
      toast.error("Bitte Name des Unterzeichners eingeben.");
      return;
    }
    onSubmit({ dataUrl: sigRef.current.toDataURL("image/png"), signer: signer.trim() });
  };

  return (
    <div className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-white p-5" data-testid="gdv-sign-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl brand-gradient-bg text-white flex items-center justify-center shrink-0">
          <PenLine className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">Versicherungsabfrage einrichten</div>
          <div className="font-display font-semibold text-base mt-0.5">GDV-Antrag unterschreiben &amp; senden</div>
          <div className="text-xs text-zinc-600 mt-1 max-w-prose">
            Mit Ihrer Unterschrift beantragen wir für Sie die Zugangsdaten zur Halterauskunft
            (Versicherungsabfrage) direkt bei der <b>GDV Dienstleistungs-GmbH</b>. Der unterschriebene
            Antrag wird automatisch per E-Mail an <b>kundenbetreuung@gdv-dl.de</b> übermittelt.
          </div>

          <div className="mt-4 rounded-xl border border-zinc-200 bg-white p-4">
            <div className="text-xs text-zinc-700 leading-relaxed">
              Hiermit beantrage ich als Kfz-Sachverständige:r den Zugang zur GDV-Halterauskunft
              und bestätige mein berechtigtes Interesse. Die Nutzung der Zugangsdaten erfolgt
              ausschließlich im Rahmen meiner Sachverständigentätigkeit.
            </div>
            <div className="flex items-center justify-between gap-2 mt-3 mb-2">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">Unterschrift Antragsteller:in</div>
              <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid="gdv-signature-clear">
                <RotateCcw className="h-4 w-4 mr-1" /> Löschen
              </Button>
            </div>
            <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 overflow-hidden">
              <SignaturePad
                ref={sigRef}
                penColor="#0b1320"
                onEnd={() => setSigEmpty(sigRef.current?.isEmpty() ?? true)}
                canvasProps={{
                  className: "w-full h-40 touch-none",
                  style: { touchAction: "none", display: "block", width: "100%", height: "10rem" },
                  "data-testid": "gdv-signature-canvas",
                }}
              />
            </div>
            <div className="mt-3">
              <Field label="Name des Unterzeichners *">
                <Input
                  value={signer}
                  onChange={(e) => setSigner(e.target.value)}
                  placeholder="Vor- und Nachname"
                  data-testid="gdv-signer-name"
                />
              </Field>
            </div>
          </div>

          <div className="text-[11px] text-zinc-500 mt-2">
            Die Unterschrift wird ausschließlich zweckgebunden für diesen Antrag verwendet und
            rechtssicher mit Zeitstempel und IP-Adresse dokumentiert.
          </div>

          <Button
            onClick={submit}
            disabled={busy}
            className="mt-3 brand-gradient-bg text-white"
            data-testid="gdv-sign-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PenLine className="h-4 w-4 mr-2" />}
            Antrag unterschreiben &amp; an GDV senden
          </Button>
        </div>
      </div>
    </div>
  );
}


function GdvRequestCard({ request, hasCreds, onCreate, busy }) {
  const STATUS_FLOW = ["pending", "requested", "delivered", "completed"];
  const FLOW_LABELS = {
    pending: "Antrag bei uns eingegangen",
    requested: "Bei der GDV beantragt",
    delivered: "Schreiben unterwegs zu Ihnen",
    completed: "Zugang aktiv",
  };

  if (hasCreds && (!request || request.status === "completed")) {
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-5 flex items-start gap-3" data-testid="gdv-request-card-done">
        <div className="h-10 w-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">Versicherungsabfrage aktiv</div>
          <div className="font-display font-semibold text-base mt-0.5">
            Sie können jetzt direkt im Case-Editor Versicherungsabfragen durchführen.
          </div>
          <div className="text-xs text-zinc-600 mt-1">
            Ihre Zugangsdaten sind hinterlegt. Bei Problemen mit der Abfrage prüfen Sie unten Requestor + Passwort.
          </div>
        </div>
      </div>
    );
  }

  if (!request || request.status === "cancelled") {
    return (
      <div className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-white p-5" data-testid="gdv-request-card-empty">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl brand-gradient-bg text-white flex items-center justify-center shrink-0">
            <Plug className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">Versicherungsabfrage einrichten</div>
            <div className="font-display font-semibold text-base mt-0.5">Zugangsdaten beantragen</div>
            <div className="text-xs text-zinc-600 mt-1 max-w-prose">
              Wir beantragen für Sie die Zugangsdaten direkt bei der GDV Dienstleistungs-GmbH. Sie erhalten anschließend ein Schreiben mit
              Ihrem persönlichen Requestor und Passwort, das Sie dann unten eintragen.
            </div>
            <Button onClick={onCreate} disabled={busy} className="mt-3 brand-gradient-bg text-white" data-testid="gdv-request-create">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}
              Antrag stellen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const idx = STATUS_FLOW.indexOf(request.status);
  const stepText = FLOW_LABELS[request.status] || "Wird bearbeitet …";

  return (
    <div className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-white p-5" data-testid="gdv-request-card-status">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl brand-gradient-bg text-white flex items-center justify-center shrink-0">
          <Plug className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">Versicherungsabfrage</div>
          <div className="font-display font-semibold text-base mt-0.5">Antrag in Bearbeitung — {stepText}</div>
          <div className="text-xs text-zinc-600 mt-1">
            Sobald Ihre Zugangsdaten eintreffen, tragen Sie sie unten ein und können sofort abfragen.
          </div>

          {/* Pipeline */}
          <div className="mt-4 flex items-center gap-1" data-testid="gdv-request-pipeline">
            {STATUS_FLOW.map((s, i) => (
              <div key={s} className={`flex-1 h-1.5 rounded-full ${
                i <= idx ? "bg-emerald-500" : "bg-zinc-200"
              }`} />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-500">
            <span className={idx >= 0 ? "font-semibold text-zinc-800" : ""}>1. Eingegangen</span>
            <span className={idx >= 1 ? "font-semibold text-zinc-800" : ""}>2. Bei GDV beantragt</span>
            <span className={idx >= 2 ? "font-semibold text-zinc-800" : ""}>3. Unterwegs zu Ihnen</span>
            <span className={idx >= 3 ? "font-semibold text-zinc-800" : ""}>4. Aktiv</span>
          </div>
          {request.delivered_at && request.status === "delivered" && (
            <div className="mt-3 rounded-lg bg-violet-50 border border-violet-200 p-3 text-xs text-violet-900">
              📬 Ihr Schreiben mit Requestor + Passwort wurde am {new Date(request.delivered_at).toLocaleDateString("de-DE")} versandt.
              Bitte tragen Sie die Daten unten ein, sobald Sie sie erhalten.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * SignatureProfileSection — Unterschrift des Sachverständigen.
 * Wird automatisch auf das finale Gutachten-PDF und die Dokumentations-
 * Vorschau (Schlusswort-Seite) gerendert.
 */
function SignatureProfileSection({ profile, onSaved }) {
  const sigRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);
  const [busy, setBusy] = useState(false);
  const hasSignature = !!profile?.signature_data_url;

  const save = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Bitte zuerst im Feld unterschreiben.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/profile", {
        signature_data_url: sigRef.current.toDataURL("image/png"),
      });
      onSaved?.(data.user);
      sigRef.current?.clear();
      setSigEmpty(true);
      toast.success("Unterschrift gespeichert", {
        description: "Sie erscheint ab jetzt automatisch auf jedem Gutachten.",
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me/profile", { signature_data_url: "" });
      onSaved?.(data.user);
      toast.message("Unterschrift entfernt");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-zinc-200 bg-white" data-testid="settings-signature-section">
      <div className="px-6 py-5 border-b border-zinc-100 flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl brand-gradient-bg text-white flex items-center justify-center shrink-0">
          <PenLine className="h-5 w-5" />
        </div>
        <div>
          <h2 className="font-display text-lg font-bold tracking-tight">Unterschrift für Gutachten</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            Ihre Unterschrift wird automatisch auf das finale Gutachten-PDF gesetzt (Schlusswort &amp; Unterschriften-Bereich).
          </p>
        </div>
      </div>
      <div className="px-6 py-5 space-y-4">
        {hasSignature && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-center gap-4" data-testid="profile-signature-preview">
            <img src={profile.signature_data_url} alt="Gespeicherte Unterschrift" className="h-16 object-contain bg-white rounded border border-zinc-100 px-2" />
            <div className="text-xs text-emerald-800 flex-1">
              <div className="font-semibold flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Unterschrift hinterlegt
              </div>
              <div className="text-zinc-500 mt-0.5">Wird automatisch auf jedem Gutachten verwendet.</div>
            </div>
            <Button variant="outline" size="sm" className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={remove} disabled={busy} data-testid="profile-signature-delete-btn">
              <XCircle className="h-3.5 w-3.5 mr-1.5" /> Entfernen
            </Button>
          </div>
        )}
        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">
              {hasSignature ? "Neue Unterschrift zeichnen" : "Unterschrift zeichnen"}
            </div>
            <Button type="button" variant="ghost" size="sm"
              onClick={() => { sigRef.current?.clear(); setSigEmpty(true); }}
              data-testid="profile-signature-clear-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Löschen
            </Button>
          </div>
          <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 overflow-hidden">
            <SignaturePad
              ref={sigRef}
              penColor="#0b1320"
              onEnd={() => setSigEmpty(sigRef.current?.isEmpty() ?? true)}
              canvasProps={{
                className: "w-full h-40 touch-none",
                style: { touchAction: "none", display: "block", width: "100%", height: "10rem" },
                "data-testid": "profile-signature-canvas",
              }}
            />
          </div>
          <Button onClick={save} disabled={busy || sigEmpty} className="mt-3 brand-gradient-bg text-white" data-testid="profile-signature-save-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Unterschrift speichern
          </Button>
        </div>
      </div>
    </section>
  );
}
